/**
 * Glowy Studio — tasarım belgesi (frame editor).
 *
 * Belge, motorun `feed_templates.validate_design` ile denetlediği JSON'un
 * TA KENDİSİDİR. Burada sadece tarayıcı tarafındaki varsayılanlar, başlangıç
 * tasarımları ve çizim yardımcıları durur; kural (ölçü sınırları, alan adları)
 * backend'den `/design/spec` ile gelir — iki yerde yazılırsa zamanla ayrışırlar.
 *
 * 🔑🔑 Editördeki önizleme ile üretilen kare AYNI Lato dosyalarını kullanır:
 * tarayıcı `/pixel/fonts/lato` üzerinden, PIL de aynı diski okur (server.js'te
 * bu dizin aynen sunuluyor). Buraya Google Fonts'tan Lato koyarsak metrikler
 * sessizce ayrışır ve kullanıcı kaydettiğinde yazı yerinden oynar.
 */

const API = 'https://api.glowytics.com';
const FONT_BASE = `${API}/pixel/fonts/lato`;

// Motorun DESIGN_FONT_FILES tablosuyla BİREBİR aynı dosyalar.
export const LATO = {
  300: ['Lato-Light', 'Lato-LightItalic'],
  400: ['Lato-Regular', 'Lato-Italic'],
  500: ['Lato-Medium', 'Lato-MediumItalic'],
  600: ['Lato-Semibold', 'Lato-SemiboldItalic'],
  700: ['Lato-Bold', 'Lato-BoldItalic'],
  800: ['Lato-Black', 'Lato-BlackItalic'],
};

export const WEIGHT_LABEL = {
  300: 'Light', 400: 'Regular', 500: 'Medium',
  600: 'Semibold', 700: 'Bold', 800: 'Black',
};

export const FONT_FAMILY = 'GlowyLato';

/** @font-face bloğu — belgeye BİR KEZ enjekte edilir. */
export function fontFaceCss() {
  return Object.entries(LATO).map(([w, [up, it]]) => `
@font-face{font-family:'${FONT_FAMILY}';font-style:normal;font-weight:${w};
  src:url('${FONT_BASE}/${up}.ttf') format('truetype');font-display:block}
@font-face{font-family:'${FONT_FAMILY}';font-style:italic;font-weight:${w};
  src:url('${FONT_BASE}/${it}.ttf') format('truetype');font-display:block}`).join('');
}

// ------------------------------------------------------------------ tuval
export const CANVAS_PRESETS = [
  { key: '1:1', label: 'Square', hint: 'Feed, Merchant Center', w: 1080, h: 1080 },
  { key: '4:5', label: 'Portrait', hint: 'The tallest Meta allows', w: 1080, h: 1350 },
  { key: '9:16', label: 'Story', hint: 'Stories, Reels', w: 1080, h: 1920 },
  { key: '16:9', label: 'Landscape', hint: 'Display, YouTube', w: 1920, h: 1080 },
];

let seq = 0;
export const newId = () => `l${Date.now().toString(36)}${(seq++).toString(36)}`;

// -------------------------------------------------------------- katmanlar
export const LAYER_DEFAULTS = {
  product: { type: 'product', fit: 'contain', radius: 0, bg: '', opacity: 1, rotation: 0 },
  rect: { type: 'rect', fill: '#111d3c', radius: 0, stroke: '', strokeWidth: 0, opacity: 1, rotation: 0 },
  ellipse: { type: 'ellipse', fill: '#e11d48', stroke: '', strokeWidth: 0, opacity: 1, rotation: 0 },
  image: { type: 'image', src: '', fit: 'contain', opacity: 1, rotation: 0 },
  text: {
    type: 'text', text: 'Your text', size: 48, weight: 700, italic: false,
    color: '#111111', align: 'left', lineHeight: 1.25, tracking: 0,
    uppercase: false, strike: false, maxLines: 0, opacity: 1, rotation: 0,
  },
};

export function makeLayer(type, patch = {}) {
  return { id: newId(), showIf: '', ...LAYER_DEFAULTS[type], ...patch };
}

export const LAYER_LABEL = (l) => ({
  product: 'Product',
  rect: 'Rectangle',
  ellipse: 'Ellipse',
  image: 'Image',
  text: FIELD_LABEL[(l.text || '').trim()] || (l.text || '').slice(0, 24) || 'Text',
}[l.type] || l.type);

// Sadece tek alan taşıyan metin katmanları listede okunur adla görünür.
const FIELD_LABEL = {
  '{{title}}': 'Product name', '{{price}}': 'Current price', '{{list_price}}': 'Old price',
  '%{{discount}}': 'Discount %', '{{discount}}': 'Discount %', '{{brand}}': 'Brand',
};

/** Katmanın kutusu. Yazının yüksekliği DOM'da ölçülür (measured), belgede yok. */
export function layerBox(l, measured = {}) {
  return {
    x: l.x, y: l.y, w: l.w,
    h: l.type === 'text' ? (measured[l.id] || Math.round(l.size * l.lineHeight)) : l.h,
  };
}

// -------------------------------------------------- başlangıç tasarımları
//
// Boş tuval kimseyi mutlu etmiyor: kullanıcı ilk açılışta bir yerden başlasın,
// sonra her şeyi değiştirsin. Üçü de aynı iskelet — ürün alanı + marka satırı +
// isim + fiyat — farklı ağırlıkta.

const card = (w, h) => {
  const pad = Math.round(w * 0.055);
  const inner = w - pad * 2;
  const prod = Math.round(inner * 0.88);
  const px = Math.round((w - prod) / 2);
  const py = Math.round(h * 0.135);
  return { pad, inner, prod, px, py };
};

export function starterDesigns(w = 1080, h = 1350) {
  const { pad, inner, prod, px, py } = card(w, h);
  const foot = py + prod;
  const s = w / 1080;              // ölçüleri tuval genişliğine bağla
  const px2 = (v) => Math.round(v * s);

  return [
    {
      key: 'clean',
      name: 'Clean card',
      desc: 'White card, brand line on top, price under the name',
      doc: {
        canvas: { w, h, bg: '#0f172a' },
        layers: [
          makeLayer('rect', { x: pad, y: pad, w: inner, h: h - pad * 2, radius: px2(40), fill: '#ffffff' }),
          makeLayer('text', {
            x: pad, y: py - px2(80), w: inner, text: '{{brand}}', size: px2(32), weight: 800,
            color: '#0f172a', align: 'center', tracking: 0.2, uppercase: true,
          }),
          makeLayer('product', { x: px, y: py, w: prod, h: prod, fit: 'contain', radius: px2(24) }),
          makeLayer('text', {
            x: pad + px2(40), y: foot + px2(60), w: inner - px2(80), text: '{{title}}',
            size: px2(44), weight: 600, color: '#111111', align: 'center', maxLines: 2,
          }),
          makeLayer('text', {
            x: pad + px2(40), y: h - pad - px2(120), w: inner - px2(80), text: '{{price}}',
            size: px2(58), weight: 800, color: '#0f172a', align: 'center',
          }),
        ],
      },
    },
    {
      key: 'badge',
      name: 'Sale badge',
      desc: 'Discount badge in the corner, old price crossed out',
      doc: {
        canvas: { w, h, bg: '#ffffff' },
        layers: [
          makeLayer('product', { x: px, y: py, w: prod, h: prod, fit: 'contain' }),
          makeLayer('ellipse', {
            x: w - pad - px2(220), y: py - px2(30), w: px2(200), h: px2(200),
            fill: '#e11d48', showIf: 'discount',
          }),
          makeLayer('text', {
            x: w - pad - px2(220), y: py + px2(55), w: px2(200), text: '%{{discount}}',
            size: px2(58), weight: 800, color: '#ffffff', align: 'center',
          }),
          makeLayer('text', {
            x: pad, y: foot + px2(70), w: inner, text: '{{title}}', size: px2(46),
            weight: 700, color: '#111111', align: 'left', maxLines: 2,
          }),
          makeLayer('text', {
            x: pad, y: h - pad - px2(130), w: px2(340), text: '{{list_price}}',
            size: px2(42), weight: 500, color: '#9ca3af', align: 'left', strike: true,
          }),
          makeLayer('text', {
            x: pad + px2(360), y: h - pad - px2(138), w: inner - px2(360), text: '{{price}}',
            size: px2(58), weight: 800, color: '#e11d48', align: 'left',
          }),
        ],
      },
    },
    {
      key: 'band',
      name: 'Colour band',
      desc: 'Product on a tinted ground, name and price on a band',
      doc: {
        canvas: { w, h, bg: '#f4f1ec' },
        layers: [
          makeLayer('product', { x: px, y: px2(120), w: prod, h: prod, fit: 'contain' }),
          makeLayer('rect', {
            x: 0, y: h - px2(320), w, h: px2(320), fill: '#111d3c',
          }),
          makeLayer('text', {
            x: pad, y: h - px2(250), w: inner, text: '{{title}}', size: px2(42),
            weight: 600, color: '#ffffff', align: 'center', maxLines: 1,
          }),
          makeLayer('text', {
            x: pad, y: h - px2(170), w: inner, text: '{{price}}', size: px2(62),
            weight: 800, color: '#ffffff', align: 'center',
          }),
        ],
      },
    },
    {
      key: 'blank',
      name: 'Blank',
      desc: 'Just the product area — build it from scratch',
      doc: {
        canvas: { w, h, bg: '#ffffff' },
        layers: [makeLayer('product', { x: px, y: py, w: prod, h: prod, fit: 'contain' })],
      },
    },
  ];
}

// --------------------------------------------------------------- yardımcı
export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Alan yer tutucularını örnek/gerçek değerle doldurur — DOM önizlemesi için.
 *  Motordaki `design_text` ile AYNI kural: içinde alan geçen ve alanların hepsi
 *  boş çıkan katman HİÇ çizilmez. */
const FIELD_RE = /\{\{\s*([a-z_]{1,20})\s*\}\}/g;

export function fillFields(raw, fields) {
  const hits = [...String(raw || '').matchAll(FIELD_RE)].map((m) => m[1]);
  if (!hits.length) return { text: String(raw || ''), hasField: false, allEmpty: false };
  const out = String(raw).replace(FIELD_RE, (_, k) => String(fields[k] ?? ''))
    .replace(/[ \t]{2,}/g, ' ').trim();
  return {
    text: out,
    hasField: true,
    allEmpty: !hits.some((k) => String(fields[k] ?? '').trim()),
  };
}

const trFmt = (v, cur) => {
  if (v == null) return '';
  const sym = cur === 'TRY' || cur === 'TL' ? '₺' : (cur ? `${cur} ` : '');
  const cents = Math.round(v * 100) % 100;
  const whole = Math.floor(Math.round(v * 100) / 100);
  const s = whole.toLocaleString('tr-TR');
  return sym + (cents ? `${s},${String(cents).padStart(2, '0')}` : s);
};

/** Motordaki `design_fields` ile aynı alanlar. */
export function productFields(p, brandName) {
  if (!p) {
    return {
      title: 'Aurora Kablosuz Kulaklık ANC', full_title: 'Aurora Kablosuz Kulaklık ANC',
      brand: brandName || 'Your brand', price: '₺3.499', list_price: '₺4.999',
      save: '₺1.500', discount: '30', currency: 'TRY',
    };
  }
  const price = p.price ?? null;
  const sale = p.salePrice ?? null;
  const eff = sale != null ? sale : price;
  const strip = (t) => (brandName
    ? String(t || '').replace(new RegExp(`^${brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+`, 'i'), '')
    : String(t || ''));
  const title = p.series || p.title || '';
  return {
    title: strip(title),
    full_title: title,
    brand: p.brand || brandName || '',
    price: trFmt(eff, p.currency),
    list_price: sale != null ? trFmt(price, p.currency) : '',
    save: sale != null && price ? trFmt(price - sale, p.currency) : '',
    discount: sale != null && price ? String(Math.round((1 - sale / price) * 100)) : '',
    currency: p.currency || '',
  };
}
